import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/queryKeys'
import { toast } from '@/shared/model/toast'
import type {
  ContactBulkRequest,
  ContactDetails,
  ContactFilter,
  ContactImportResult,
  ContactProfileRequest,
  ContactsResponse,
} from '@/shared/api/types'

interface ContactsPage {
  filter?: ContactFilter
  limit?: number
  offset?: number
}

function contactsQuery({ filter = {}, limit = 50, offset = 0 }: ContactsPage): string {
  const params = new URLSearchParams()
  if (filter.status) params.set('status', filter.status)
  if (filter.label !== undefined) params.set('label', filter.label)
  if (filter.q) params.set('q', filter.q)
  params.set('limit', String(limit))
  params.set('offset', String(offset))
  return params.toString()
}

export const contactsApi = {
  list: (params: ContactsPage, signal?: AbortSignal) =>
    api.get<ContactsResponse>(`/contacts?${contactsQuery(params)}`, signal),
  import: (text: string) => api.post<ContactImportResult>('/contacts/import', { text }),
  bulk: (body: ContactBulkRequest) => api.post<{ affected: number }>('/contacts/bulk', body),
  details: (id: number, signal?: AbortSignal) =>
    api.get<ContactDetails>(`/contacts/${id}`, signal),
  updateProfile: (id: number, body: ContactProfileRequest) =>
    api.put<ContactDetails>(`/contacts/${id}/profile`, body),
}

export function useContacts(params: ContactsPage) {
  const query = contactsQuery(params)
  return useQuery({
    queryKey: [...queryKeys.contacts, query],
    queryFn: ({ signal }) => contactsApi.list(params, signal),
    placeholderData: (previous) => previous,
  })
}

export function useContact(id: number | null) {
  return useQuery({
    queryKey: [...queryKeys.contact, id],
    queryFn: ({ signal }) => contactsApi.details(id as number, signal),
    enabled: id !== null,
  })
}

export function useImportContacts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (text: string) => contactsApi.import(text),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.contacts })
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось загрузить номера')
    },
  })
}

export function useBulkContacts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: ContactBulkRequest) => contactsApi.bulk(body),
    onSuccess: ({ affected }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.contacts })
      void queryClient.invalidateQueries({ queryKey: queryKeys.contact })
      void queryClient.invalidateQueries({ queryKey: queryKeys.clientEvents })
      toast.success(`Обновлено: ${affected}`)
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось обновить базу')
    },
  })
}

export function useUpdateContactProfile(id: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: ContactProfileRequest) => contactsApi.updateProfile(id, body),
    onSuccess: (details) => {
      queryClient.setQueryData([...queryKeys.contact, id], details)
      void queryClient.invalidateQueries({ queryKey: queryKeys.contacts })
      void queryClient.invalidateQueries({ queryKey: queryKeys.clientEvents })
      toast.success('Карточка обновлена')
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'Не удалось сохранить карточку')
    },
  })
}
